import { useEffect, useLayoutEffect, useRef, useState, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical, Users, Package, FileText, ShoppingCart } from 'lucide-react';

interface IconProps { size?: number }
type IconCmp = ComponentType<IconProps>;

interface Atalho { to: string; label: string; icon: IconCmp }

const ATALHOS: Atalho[] = [
  { to: '/cadastros/clientes', label: 'Clientes', icon: Users },
  { to: '/produtos', label: 'Produtos', icon: Package },
  { to: '/orcamentos', label: 'Orcamentos', icon: FileText },
  { to: '/caixa', label: 'Caixa', icon: ShoppingCart },
];

// Botao "..." no canto superior esquerdo do header. Abre popover com cards
// azuis dos atalhos rapidos pras telas mais usadas. Visivel pra todos.
export function AtalhosRapidosMenu() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const painelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function fora(e: MouseEvent) {
      const t = e.target as Node;
      if (painelRef.current?.contains(t)) return;
      if (btnRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 6, left: r.left });
  }, [open]);

  function abrir(to: string) {
    navigate(to);
    setOpen(false);
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="btn-icon-header"
        title="Atalhos"
        aria-label="Atalhos"
      >
        <MoreVertical size={24} />
      </button>
      {open && pos && (
        <div
          ref={painelRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            zIndex: 1000,
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 75px)',
            gap: 10,
            padding: 12,
            backgroundColor: 'var(--panel-bg, #fff)',
            border: '1px solid var(--border, rgba(0,0,0,0.12))',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          }}
        >
          {ATALHOS.map(({ to, label, icon: Icon }) => (
            <button
              key={to}
              type="button"
              onClick={() => abrir(to)}
              title={label}
              style={{
                width: 75,
                height: 75,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                padding: 5,
                background: 'linear-gradient(180deg, var(--primary) 0%, var(--primary-hover) 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
                cursor: 'pointer',
                transition: 'transform 80ms, box-shadow 80ms',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 10px rgba(0, 0, 0, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = '';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.2)';
              }}
            >
              <span style={{
                width: 33, height: 33, borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.95)',
                color: 'var(--primary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon size={18} />
              </span>
              <span style={{
                fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
                textTransform: 'uppercase', lineHeight: 1,
                whiteSpace: 'nowrap',
              }}>{label}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}
